'use client';

import { QuotesCodeMapping } from '@/constants/quote/quote';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useRef, useState } from 'react';
import ForexListSkeleton from './ForexListSkeleton';
import { Section } from '@/components/atoms/Section';
import { QuotesContext } from '../ForexListChart/index.client';
import ListTabs from '@/components/molecules/ForexList/ListTabs';
import { quotesColumns, performanceColumns } from '@/components/molecules/ForexList/columns';
import { saveColumn } from '@/components/atoms/ReactTable/columns';
import CurrencySearchInput from '../CurrencySearchInput';
import useGetUserSave from '@/components/atoms/ReactTable/hooks/useGetUserSave';
import { getForexList } from './getForexList';
import ReactTable from '@/components/atoms/ReactTable';
import { SubTab } from './config';
import { sendGTM, GTMEventKey, CustomEventName, sendPageView } from '@/utils/gtm';

enum ApiFormat {
  D_FORMAT = 'D_FORMAT',
  C_FORMAT = 'C_FORMAT'
}

type DFormat = {
  [QuotesCodeMapping.SYMBOL]: string;
  [QuotesCodeMapping.TRADE_TIME]: number;
  [QuotesCodeMapping.CHINESE_NAME]: string;
  [QuotesCodeMapping.ENGLISH_NAME]: string;
  [QuotesCodeMapping.BID]: string;
  [QuotesCodeMapping.ASK]: string;
  [QuotesCodeMapping.CHANGE_PERCENT_V2]: string;
  [QuotesCodeMapping.CHANGE_V2]: string;
};

type TabsProps = { tab: string; subTab: string };
type ListProps = TabsProps & { search: string; userSaveList: string[] };

// saveColumn
const gtmEvent = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: '%p_看看匯率自選'
};

const List = ({ tab, subTab, search, userSaveList }: ListProps) => {
  const { data } = useQuery({
    queryKey: ['forex-list-chart', tab, search ? SubTab.ALL : subTab],
    queryFn: getForexList,
    select: res => res?.data?.items || [],
    refetchOnWindowFocus: false,
    staleTime: 60000,
    gcTime: 60000,
    placeholderData: prev => prev,
    refetchInterval: 60000
  });
  const context = useContext(QuotesContext);
  const diffColumns = tab === ApiFormat.D_FORMAT ? quotesColumns : performanceColumns;
  const columns = [saveColumn(userSaveList, gtmEvent), ...diffColumns];

  const onClickRow = useCallback((row: DFormat) => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_FOREX,
      [GTMEventKey.SECTION]: '%p_看看匯率自選',
      [GTMEventKey.CLICK_ITEM]: `${tab}_${subTab}_${row[QuotesCodeMapping.SYMBOL]}`
    });

    context?.setStore(v => ({ ...v, symbol: row[QuotesCodeMapping.SYMBOL] }));
    const [, code] = row[QuotesCodeMapping.SYMBOL].split(':');
    sendPageView(`/forex/reuters/main/${code}`);
    history.pushState({}, '', `/forex/reuters/main/${code}`);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='mt-4'>
      <ReactTable
        data={data}
        columns={columns}
        onClick={onClickRow}
        enablePagination
        Skeleton={<ForexListSkeleton />}
        className='h-[62px]'
        headerClassName='h-[46px]'
        search={search}
      />
    </div>
  );
};

const ForexList = () => {
  const [searchWord, setSearchWord] = useState<string>('');
  const isSearch = searchWord !== '';
  const inputRef = useRef<{
    focus: () => void;
    clear: () => void;
  } | null>(null);

  const onClearSearch = () => {
    setSearchWord('');
    inputRef?.current?.clear();
  };

  const onSearch = (value: string) => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.SEARCH,
      [GTMEventKey.SECTION]: '%p_看看匯率',
      [GTMEventKey.CLICK_ITEM]: value
    });

    setSearchWord(value);
  };

  // 取得使用者自選列表
  const { data: userSaveList } = useGetUserSave();

  return (
    <div className='w-full max-w-[464px]'>
      <Section title='看看匯率' enabledGTM rightContent={<CurrencySearchInput onSearch={onSearch} ref={inputRef} />} />
      <ListTabs isSearch={isSearch} clearSearch={onClearSearch}>
        {(props: TabsProps) => <List {...props} search={searchWord} userSaveList={userSaveList} />}
      </ListTabs>
    </div>
  );
};

export default ForexList;
